const GEO_CACHE_KEY = 'hcva_visitor_geo';

const EMPTY_GEO = {
  ip: '',
  city: '',
  region: '',
  country: '',
  countryCode: '',
  isp: '',
  loaded: false,
  error: false,
};

const CACHE_TTL_MS = 60 * 1000;

function normalizeGeo(data) {
  return {
    ip: data.ip || data.query || '',
    city: data.city || '',
    region: data.region || data.regionName || '',
    country: data.country_name || data.country || '',
    countryCode: (data.country_code || data.countryCode || '').toUpperCase(),
    isp: data.org || data.isp || '',
    loaded: true,
    error: false,
  };
}

function readCachedGeo() {
  try {
    const raw = sessionStorage.getItem(GEO_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ip) return null;
    if (parsed.cachedAt && Date.now() - parsed.cachedAt > CACHE_TTL_MS) return null;
    return { ...parsed, loaded: true, error: false };
  } catch {
    return null;
  }
}

function writeCachedGeo(geo) {
  try {
    sessionStorage.setItem(GEO_CACHE_KEY, JSON.stringify({ ...geo, cachedAt: Date.now() }));
  } catch {
    /* ignore */
  }
}

async function fetchFromIpApiCo(signal) {
  const res = await fetch(`https://ipapi.co/json/?ts=${Date.now()}`, {
    signal,
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`ipapi ${res.status}`);
  const data = await res.json();
  if (data.error) throw new Error(data.reason || 'ipapi error');
  return normalizeGeo(data);
}

async function fetchFromIpApiCom(signal) {
  // ip-api's free endpoint is HTTP-only and gets blocked on HTTPS deployments.
  throw new Error('ip-api disabled on https sites');
}

async function fetchFromIpWhoIs(signal) {
  const res = await fetch('https://ipwho.is/', { signal, cache: 'no-store' });
  if (!res.ok) throw new Error(`ipwho ${res.status}`);
  const data = await res.json();
  if (data.success === false) throw new Error(data.message || 'ipwho error');
  return normalizeGeo({
    ip: data.ip,
    city: data.city,
    region: data.region,
    country_name: data.country,
    country_code: data.country_code,
    org: data.connection?.isp || data.connection?.org || '',
  });
}

async function fetchFromIpify(signal) {
  const res = await fetch(`https://api64.ipify.org?format=json&ts=${Date.now()}`, {
    signal,
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`ipify ${res.status}`);
  const data = await res.json();
  return normalizeGeo({ ip: data.ip });
}

/**
 * Resolve the visitor's current public IP and approximate location.
 * Use force=true on the visible IP screen so VPN/network changes are reflected.
 */
export async function fetchVisitorGeo({ force = false } = {}) {
  const cached = !force ? readCachedGeo() : null;
  if (cached) return cached;

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 8000);

  try {
    const providers = [fetchFromIpWhoIs, fetchFromIpApiCo, fetchFromIpify, fetchFromIpApiCom];
    let lastErr = null;
    for (const provider of providers) {
      try {
        const geo = await provider(controller.signal);
        if (geo?.ip) {
          writeCachedGeo(geo);
          return geo;
        }
      } catch (err) {
        lastErr = err;
      }
    }
    throw lastErr || new Error('ip lookup failed');
  } catch {
    const fallback = { ...EMPTY_GEO, loaded: true, error: true };
    writeCachedGeo(fallback);
    return fallback;
  } finally {
    window.clearTimeout(timeout);
  }
}

export function formatLocationLine(geo) {
  if (!geo) return '—';
  const parts = [geo.city, geo.region, geo.country].filter(Boolean);
  if (!parts.length) return 'Unknown location';
  return parts.join(', ');
}
